import { boundsContain, boundsIntersect, boundsOfPoints, type Bounds } from './bounds';
import { intersect } from './intersect';
import { segment, type Segment } from './segment';
import { bounds, endpoints, liesOn, type Shape } from './shape';
import { vec, type Vec2 } from './vec2';

/** Where something drawn stands against a box: wholly inside, touching its area, or clear. */
export type BoxPlace = 'inside' | 'crossing' | null;

const cornersOf = ({ min, max }: Bounds): Vec2[] => [
  min,
  vec(max.x, min.y),
  max,
  vec(min.x, max.y),
];

const sidesOf = (box: Bounds): Segment[] => {
  const corners = cornersOf(box);
  return corners.map((corner, i) => segment(corner, corners[(i + 1) % 4]!));
};

/** Whether `p` is in the box, its edges included. */
export const boxHolds = ({ min, max }: Bounds, p: Vec2): boolean =>
  p.x >= min.x && p.x <= max.x && p.y >= min.y && p.y <= max.y;

/**
 * Where a shape stands against a box, by the shape itself rather than its bounds: a circle
 * around the box, or a line passing near its corner, is clear of it.
 */
export function shapeInBox(shape: Shape, box: Bounds): BoxPlace {
  const around = bounds(shape);
  if (!boundsIntersect(around, box)) return null;
  if (boundsContain(box, around)) return 'inside';
  // Not wholly inside, so it touches only if part of it is: an end in the box, a crossing of
  // an edge, or a corner on it, which also catches a line lying along an edge.
  const touches =
    endpoints(shape).some((p) => boxHolds(box, p)) ||
    sidesOf(box).some((side) => intersect(shape, side).length > 0) ||
    cornersOf(box).some((corner) => liesOn(shape, corner));
  return touches ? 'crossing' : null;
}

/** Whether `p` is inside a closed polygon, by counting the edges a ray from it crosses. */
function polygonHolds(points: readonly Vec2[], p: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const a = points[i]!;
    const b = points[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x)
      inside = !inside;
  }
  return inside;
}

/** Where a filled polygon, such as an arrowhead or a text's area, stands against a box. */
export function polygonInBox(points: readonly Vec2[], box: Bounds): BoxPlace {
  if (points.length === 0) return null;
  const around = boundsOfPoints(points);
  if (!boundsIntersect(around, box)) return null;
  if (boundsContain(box, around)) return 'inside';
  const edges = points.map((point, i) => segment(point, points[(i + 1) % points.length]!));
  const touches =
    points.some((p) => boxHolds(box, p)) ||
    cornersOf(box).some((corner) => polygonHolds(points, corner)) ||
    edges.some((edge) => sidesOf(box).some((side) => intersect(edge, side).length > 0)) ||
    cornersOf(box).some((corner) => edges.some((edge) => liesOn(edge, corner)));
  return touches ? 'crossing' : null;
}
